import { type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, ShieldX } from 'lucide-react';

import { AuthBrandShell } from '@/components/auth/AuthBrandShell';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { ROUTES } from '@/lib/constants/routes';

export function Unauthorized(): ReactElement {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleGoToLogin = async (): Promise<void> => {
    await signOut();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  return (
    <AuthBrandShell page="unauthorized" heroAccessibilityTitle="Access denied">
      <section className="w-full max-w-md" aria-labelledby="unauthorized-heading">
        <div className="rounded-2xl border border-white/30 bg-white/90 p-6 shadow-xl backdrop-blur-md">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/15">
              <ShieldX className="h-6 w-6 text-destructive" aria-hidden />
            </div>
            <h2
              id="unauthorized-heading"
              className="text-foreground text-2xl font-semibold tracking-tight"
            >
              Access Denied
            </h2>
            <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
              Your account does not have the Booking Admin or System Admin role needed to use this
              app. Please sign in with an approved account or contact your administrator to update
              your access.
            </p>
          </div>
          <div className="mt-6 flex flex-col gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleGoToLogin()}
              className="cursor-pointer rounded-[40px] border-[#1745DF] py-6 text-[#1745DF] hover:bg-[#1745DF]/10"
            >
              <LogIn className="mr-2 h-4 w-4" aria-hidden />
              Sign out and use another account
            </Button>
          </div>
        </div>
      </section>
    </AuthBrandShell>
  );
}
